import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Car, HandCoins, Calendar, Search } from 'lucide-react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { LOGO_URL } from '../config/dealership';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Sync search query from URL when on /cars page
  useEffect(() => {
    if (location.pathname === '/cars') {
      const q = searchParams.get('q') || '';
      setSearchQuery(q);
    }
  }, [location.pathname, searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/cars?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsMobileMenuOpen(false);
    } else {
      navigate('/cars');
      setIsMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { label: 'Home', href: '/', isRoute: true },
    { label: 'Buy Cars', href: '/cars', isRoute: true, icon: Car },
    { label: 'Sell Car', href: '/sell-your-car', isRoute: true, icon: HandCoins },
    { label: 'Book', href: '/repairs', isRoute: true, icon: Calendar },
    { label: 'Contact', href: '/contact', isRoute: true },
  ];

  const isHomePage = location.pathname === '/';

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled || !isHomePage
          ? 'bg-[#020405]/90 backdrop-blur-md border-b border-white/5'
          : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center"
            data-testid="logo-link"
          >
            <motion.img 
              whileHover={{ scale: 1.02 }}
              src={LOGO_URL}
              alt="CMAC & Son Autos"
              className="h-10 md:h-12 w-auto"
              data-testid="header-logo"
            />
          </Link>

          {/* Desktop Navigation + Search */}
          <div className="hidden md:flex items-center gap-6">
            {/* Navigation Links */}
            <nav className="flex items-center gap-6" data-testid="desktop-nav">
              {navLinks.map((link, index) => (
                link.isRoute ? (
                  <motion.div
                    key={link.label}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                    whileHover={{ y: -2 }}
                  >
                    <Link
                      to={link.href}
                      className={`text-xs font-medium transition-colors uppercase tracking-widest flex items-center gap-1.5 ${
                        location.pathname === link.href 
                          ? 'text-[#48CCCD]' 
                          : 'text-white/80 hover:text-[#48CCCD]'
                      }`}
                      data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.icon && <link.icon size={14} strokeWidth={1.5} />}
                      {link.label}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.a
                    key={link.label}
                    href={link.href}
                    className="text-xs font-medium text-white/80 hover:text-[#48CCCD] transition-colors uppercase tracking-widest flex items-center gap-1.5"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                    whileHover={{ y: -2 }}
                    data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.icon && <link.icon size={14} strokeWidth={1.5} />}
                    {link.label}
                  </motion.a>
                )
              ))}
            </nav>

            {/* Desktop Search Bar */}
            <motion.form
              onSubmit={handleSearch}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="relative"
              data-testid="desktop-search-form"
            >
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search make, model, year..."
                className="w-48 lg:w-56 h-9 pl-9 pr-3 bg-white/5 border border-white/10 text-sm text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none focus:border-[#48CCCD]/50 focus:bg-white/10 transition-all"
                data-testid="desktop-search-input"
              />
              <Search 
                size={16} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" 
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#48CCCD] transition-colors"
                aria-label="Search"
                data-testid="desktop-search-btn"
              >
                <Search size={14} />
              </button>
            </motion.form>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white/80 hover:text-[#48CCCD] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <motion.nav
        initial={false}
        animate={{
          height: isMobileMenuOpen ? 'auto' : 0,
          opacity: isMobileMenuOpen ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
        className="md:hidden overflow-hidden bg-[#0A0F14]/95 backdrop-blur-md border-b border-white/5"
        data-testid="mobile-nav"
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {/* Mobile Search Bar */}
          <form 
            onSubmit={handleSearch}
            className="relative"
            data-testid="mobile-search-form"
          >
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search make, model, year..."
              className="w-full h-11 pl-10 pr-4 bg-white/5 border border-white/10 text-sm text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none focus:border-[#48CCCD]/50 transition-all"
              data-testid="mobile-search-input"
            />
            <Search 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" 
            />
            <button
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#48CCCD] text-black p-1.5 hover:bg-white transition-colors"
              aria-label="Search"
              data-testid="mobile-search-btn"
            >
              <Search size={14} />
            </button>
          </form>

          {/* Mobile Nav Links */}
          {navLinks.map((link) => (
            link.isRoute ? (
              <Link
                key={link.label}
                to={link.href}
                className={`text-sm font-medium transition-colors uppercase tracking-widest flex items-center gap-2 ${
                  location.pathname === link.href 
                    ? 'text-[#48CCCD]' 
                    : 'text-white/80 hover:text-[#48CCCD]'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.icon && <link.icon size={16} strokeWidth={1.5} />}
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-white/80 hover:text-[#48CCCD] transition-colors uppercase tracking-widest flex items-center gap-2"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.icon && <link.icon size={16} strokeWidth={1.5} />}
                {link.label}
              </a>
            )
          ))}
        </div>
      </motion.nav>
    </motion.header>
  );
};

export default Header;
